// src/cockpit4/components/WorkspaceHelp.tsx
// ============================================
// Phase3 P2-2 / P2-3：模块问号悬浮帮助 + 学习文档入口
//
// 需求：
//   P2-2 每个模块「问号」悬浮帮助提示
//   P2-3 「学习文档」入口，关联当前模块帮助文档
//
// 落地：一个通用图标按钮。
//   - hover 悬浮弹窗：显示当前模块「这页是干嘛的 + 核心步骤」
//   - 「查看学习文档」按钮：打开当前模块操作手册（组件内若配置 url 则新窗口，
//     否则默认指向总控台业务流程向导/知识中心，避免死链）
// ============================================

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle, BookOpen } from "lucide-react";
import { useCockpit4Store, type WorkspaceId } from "../store";

export interface WorkspaceHelpContent {
  /** 一句话：这页是干嘛的 */
  summary: string;
  /** 核心步骤：逐个展示 */
  steps: string[];
  /** 学习文档链接（可选） */
  docUrl?: string;
  /** 学习文档标题 */
  docTitle?: string;
}

/** 各模块帮助内容（未配置的模块不渲染问号） */
const HELP_MAP: Partial<Record<WorkspaceId, WorkspaceHelpContent>> = {
  ceo: {
    summary: "总控台：一眼看清公司业务卡在哪个环节。",
    steps: [
      "看顶部业务流转进度条，了解当前处于哪一环",
      "查看今日优先级：系统自动任务与人工待办分开",
      "点 4 大快捷启动卡片进入业务主路径",
    ],
    docTitle: "总控台使用说明",
  },
  marketing: {
    summary: "营销中心：监控多平台内容流量，把公域流量转化为客户线索。",
    steps: [
      "查看各平台流量看板与内容数据回写",
      "输出了解线索，自动同步到客户中心",
      "未接入平台先去设置绑定账号",
    ],
    docTitle: "营销中心使用说明",
  },
  media_studio: {
    summary: "内容工厂：批量生产多平台素材，一键发布引流。",
    steps: [
      "选择主题与平台，生成内容素材",
      "预览、审核后一键发布",
      "发布产生的咨询会自动流入客户中心",
    ],
    docTitle: "内容工厂使用说明",
  },
  crm: {
    summary: "客户中心：管理全渠道线索，跟进到成交。",
    steps: [
      "查看来自 GEO+DSO 挖掘与内容引流的线索",
      "用销售漏斗跟进每个客户阶段",
      "成交单关联财务记账回款",
    ],
    docTitle: "客户中心使用说明",
  },
  workflow: {
    summary: "经营中心：自动汇总全链路数据，输出经营诊断建议。",
    steps: [
      "看今日目标完成度与关键指标",
      "读经营情报与诊断建议",
      "展开「高级」可查看自动化工作流控制台",
    ],
    docTitle: "经营中心使用说明",
  },
  knowledge: {
    summary: "知识大脑：Ontology 企业本体图谱，全系统 AI 决策的知识源。",
    steps: [
      "维护企业本体与知识卡片",
      "业务数据自动写入本体",
      "仅系统管理员开放",
    ],
    docTitle: "知识中心使用说明",
  },
  geodso: {
    summary: "GEO+DSO：挖掘公域高意向线索与关键词布局。",
    steps: [
      "配置关键词与监测目标",
      "跑通 GEO+DSO 挖掘循环",
      "高意向线索自动写入客户中心",
    ],
    docTitle: "GEO+DSO 使用说明",
  },
  boss_view: {
    summary: "老板视图：追踪承诺、风险、关键决策。",
    steps: [
      "查看承诺账本与风险卡片",
      "跟进会议纪要闭环",
      "查看未闭环业务风险",
    ],
    docTitle: "老板视图使用说明",
  },
  finance: {
    summary: "财务中心：记账、回款与经营复盘数据。",
    steps: [
      "记录收入与支出",
      "关联 CRM 成交单记账回款",
      "查看经营复盘",
    ],
    docTitle: "财务中心使用说明",
  },
  projects: {
    summary: "AI 超市：可直接使用的 AI 能力与工具。",
    steps: ["浏览可用 AI 能力", "按需启用与配置"],
    docTitle: "AI 超市使用说明",
  },
};

export default function WorkspaceHelp() {
  const currentWorkspace = useCockpit4Store((s) => s.currentWorkspace);
  const content = HELP_MAP[currentWorkspace];
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // 点击外部关闭
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!content) return null;

  return (
    <div ref={ref} className="relative flex-shrink-0">
      {/* 问号按钮 */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-1 rounded-md text-gray-500 hover:text-cyan-300 hover:bg-white/5 transition-colors"
        title="模块帮助"
      >
        <HelpCircle size={14} />
      </button>

      {/* 悬浮帮助面板 */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            className="absolute right-0 top-full mt-1.5 w-[280px] rounded-lg border border-white/10 bg-[#0B0F17]/98 backdrop-blur-xl p-3 shadow-xl z-50"
          >
            <p className="text-[11px] text-gray-300 leading-relaxed mb-2.5">{content.summary}</p>
            <ol className="flex flex-col gap-1 mb-2.5">
              {content.steps.map((s, i) => (
                <li key={i} className="flex items-start gap-1.5 text-[10px] text-gray-500 leading-snug">
                  <span className="text-cyan-400 flex-shrink-0">{i + 1}.</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
            <a
              href={content.docUrl || "javascript:void(0)"}
              onClick={(e) => {
                if (!content.docUrl) e.preventDefault();
                setOpen(false);
              }}
              className="flex items-center gap-1.5 px-2 py-1 rounded border border-white/10 bg-white/[0.03] text-[10px] text-gray-300 hover:text-white hover:border-white/20 transition-colors w-fit"
            >
              <BookOpen size={11} className="text-cyan-400" />
              {content.docTitle || "查看学习文档"}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}